import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import {
  AdminOrganizationUser,
  CreateAdminOrganizationUserPayload
} from '@utils/types/admin';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export const ADMIN_ORGANIZATION_USERS_QUERY_KEY = 'adminOrganizationUsers';

type CreateUserParams = {
  organizationId: number | string;
  payload: CreateAdminOrganizationUserPayload;
};

/**
 * Hook to create/invite a user in an on-prem organization
 *
 * POST /api/admin/on-prem/organizations/{organization_id}/users
 */
export const useCreateAdminOrganizationUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createUser = async ({ organizationId, payload }: CreateUserParams) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/users`;
    const response = await Axios.post<BaseResponse<AdminOrganizationUser>>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    BaseResponse<AdminOrganizationUser>,
    AxiosError<BaseError>,
    CreateUserParams
  >(createUser, {
    onSuccess: (_, variables) => {
      toast.success(t('user-created-successfully'));
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        variables.organizationId
      ]);
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        String(variables.organizationId)
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-create-user');
      toast.error(String(errorMessage));
    }
  });
};
